import { Button, Form, Tab, Tabs } from "react-bootstrap";
import { Task, SharePermission, TaskShareUser } from "../interfaces/Task";
import { useEffect, useState } from "react";
import { User, emptyUser } from "../interfaces/User";
import parse from 'html-react-parser';
import { useNavigate } from "react-router-dom";
import { obscureEmail } from "../../utils/utils";
import { UsersGroup } from "../interfaces/UsersGroup";
import { useTranslation } from "react-i18next";
import { Overlay } from "../elements/Overlay";
import { handleApiError, safeJsonParse } from "../../utils/api";

interface ShareTaskProps {
    task: Task,
    shareCallback: CallableFunction;
    closeCallback: CallableFunction;
}

interface GroupShareInfo {
    id: string;
    label: string;
    memberCount: number;
}

export default function ShareTask(props: ShareTaskProps) {
    const { t } = useTranslation();

    const navigate = useNavigate();

    const userId = localStorage.getItem('userId');

    const [user, setUser] = useState<User>(emptyUser);
    const [orgShare, setOrgShare] = useState(false);
    const [usersFound, setUsersFound] = useState<any[]>([]);
    const [usersSelected, setUsersSelected] = useState<TaskShareUser[]>([]);
    const [tabKey, setTabKey] = useState('concrete-users');
    const [groupsFound, setGroupsFound] = useState<any[]>([]);
    const [groupsSelected, setGroupsSelected] = useState<GroupShareInfo[]>([]);
    const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
    const [groupMembers, setGroupMembers] = useState<{[key: string]: any[]}>({});

    const handleShare = async () => {
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + localStorage.getItem('token')
        };

        try {
            // Share with individual users
            const usersResponse = await fetch(import.meta.env.VITE_API_URL + '/v1/task-shares', {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    task: props.task._id,
                    users: usersSelected.map(u => ({
                        id: u.id,
                        permission: u.permission
                    }))
                })
            });

            if (handleApiError(usersResponse, navigate)) {
                return;
            }

            // Share with groups
            const groupsPayload = {
                task: props.task._id,
                groups: groupsSelected.map(g => g.id)
            };
            const groupsResponse = await fetch(import.meta.env.VITE_API_URL + '/v1/task-shares/groups', {
                method: 'POST',
                headers,
                body: JSON.stringify(groupsPayload)
            });

            if (handleApiError(groupsResponse, navigate)) {
                return;
            }

            await groupsResponse.json();

            props.shareCallback();
        } catch (error) {
            console.error('Error sharing task:', error);
            navigate('/');
        }
    }

    const handleClose = () => {
        props.closeCallback();
    }

    const handleShareWithOrgChange = (event: any) => {
        setOrgShare(event.target.checked);
        setUsersFound([]);
    }

    const handleUsersSearch = (event: any) => {
        if (event.target.value.length < 3) {
            setUsersFound([]);
        } else {
            let reqUrl = import.meta.env.VITE_API_URL + '/v1/user/search';
            let queryParams: string[] = [];
            if (orgShare) {
               queryParams.push('org=' + user.org._id);
            }
            queryParams.push('search=' + event.target.value);
            const requestOptionsPost = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            };
            reqUrl += '?' + queryParams.join('&');
            fetch(reqUrl, requestOptionsPost)
                .then(response => {
                    if (handleApiError(response, navigate)) {
                        return null;
                    }
                    return safeJsonParse(response);
                })
                .then(data => {
                    if (!data) return;
                    const newUsersFound = data.users.map((u: any) => {
                        return {
                            id: u.id,
                            label: obscureEmail(u.email) + (u.name ? ' (' + u.name + ')' : '')
                        }
                    });
                    setUsersFound(newUsersFound);
                });
        }
    }

    const handleSelectUser = (event: any) => {
        const id = event.target.id.replace('userFound-', '');
        const selectedUser = usersFound.find((u: any) => u.id == id);
        const newUsersSelected: TaskShareUser[] = [
            ...usersSelected,
            {
                ...selectedUser,
                permission: 'view' as SharePermission
            }
        ];
        setUsersSelected(newUsersSelected);
    }

    const handleUnselectUser = (event: any) => {
        const id = event.target.id.replace('userShared-', '');
        const newUsersSelected = usersSelected.filter((u: TaskShareUser) => u.id != id);
        setUsersSelected(newUsersSelected);
    }

    const handleUserPermissionChange = (userId: string, permission: SharePermission) => {
        setUsersSelected(usersSelected.map(u =>
            u.id === userId ? { ...u, permission } : u
        ));
    }

    const handleSelectGroup = (event: any) => {
        const id = event.target.id.replace('groupFound-', '');
        const selectedGroup = groupsFound.find((g: any) => g._id == id);
        // Support both new (members) and legacy (users) structure
        const memberCount = selectedGroup.memberCount || selectedGroup.members?.length || selectedGroup.users?.length || 0;
        const newGroupsSelected: GroupShareInfo[] = [
            ...groupsSelected,
            {
                id: selectedGroup._id,
                label: selectedGroup.name,
                memberCount: memberCount
            }
        ];
        setGroupsSelected(newGroupsSelected);
        // Auto-expand to show members
        setExpandedGroup(selectedGroup._id);
    }

    const handleUnselectGroup = (event: any) => {
        const id = event.target.id.replace('groupShared-', '');
        const newGroupsSelected = groupsSelected.filter((g: GroupShareInfo) => g.id != id);
        setGroupsSelected(newGroupsSelected);
        if (expandedGroup === id) {
            setExpandedGroup(null);
        }
    }

    const handleToggleGroupExpand = (groupId: string) => {
        if (expandedGroup === groupId) {
            setExpandedGroup(null);
        } else {
            setExpandedGroup(groupId);
            // Load members if not already loaded
            if (!groupMembers[groupId]) {
                loadGroupMembers(groupId);
            }
        }
    }

    const loadGroupMembers = (groupId: string) => {
        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            }
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/users-groups/group/' + groupId + '/members', requestOptions)
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (!data) return;
                setGroupMembers(prev => ({
                    ...prev,
                    [groupId]: data.members.map((member: any) => ({
                        id: member.id,
                        email: member.email,
                        name: member.name || '',
                        label: obscureEmail(member.email) + (member.name ? ' (' + member.name + ')' : ''),
                        permission: member.permission
                    }))
                }));
            });
    }

    useEffect(() => {
        const userId = localStorage.getItem('userId') ?? null;

        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            }
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/user/' + userId, requestOptions)
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (!data) return;
                setUser(data);
            });

        // Load users the task is shared with
        fetch(import.meta.env.VITE_API_URL + '/v1/task-shares/task/' + props.task._id, requestOptions)
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (!data) return;
                const newUsersSelected: TaskShareUser[] = data.map((u: any) => {
                    // Support both name and firstname/lastname formats
                    let name = '';
                    if (u.user.profile?.name) {
                        name = u.user.profile.name;
                    } else if (u.user.profile) {
                        const parts: string[] = [];
                        if (u.user.profile.firstname) parts.push(u.user.profile.firstname);
                        if (u.user.profile.lastname) parts.push(u.user.profile.lastname);
                        name = parts.join(' ');
                    }
                    return {
                        id: u.user._id,
                        label: obscureEmail(u.user.email) + (name ? ' (' + name + ')' : ''),
                        permission: u.permission || 'view'
                    }
                });
                setUsersSelected(newUsersSelected);
            });

        // Load user's groups
        fetch(import.meta.env.VITE_API_URL + '/v1/users-groups/' + userId, requestOptions)
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (!data) return;
                setGroupsFound(data);
                // Load members for each group via the members endpoint
                data.forEach((group: any) => {
                    fetch(import.meta.env.VITE_API_URL + '/v1/users-groups/group/' + group._id + '/members', requestOptions)
                        .then(response => {
                            if (handleApiError(response, navigate)) {
                                return null;
                            }
                            return safeJsonParse(response);
                        })
                        .then(membersData => {
                            if (!membersData?.members) return;
                            // Update groupsFound with member count
                            setGroupsFound(prev => prev.map(g =>
                                g._id === group._id
                                    ? { ...g, memberCount: membersData.members.length }
                                    : g
                            ));
                            // Store members for display
                            setGroupMembers(prev => ({
                                ...prev,
                                [group._id]: membersData.members.map((m: any) => ({
                                    id: m.id,
                                    email: m.email,
                                    name: m.name || '',
                                    label: obscureEmail(m.email) + (m.name ? ' (' + m.name + ')' : ''),
                                    permission: m.permission
                                }))
                            }));
                        });
                });
            });

        // Load groups the task is shared with
        fetch(import.meta.env.VITE_API_URL + '/v1/task-shares/task/groups/' + props.task._id, requestOptions)
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (!data || data.length === 0) return;

                // For each shared group, load members via the members endpoint
                const groupPromises = data.map((ug: any) => {
                    return fetch(import.meta.env.VITE_API_URL + '/v1/users-groups/group/' + ug.group._id + '/members', requestOptions)
                        .then(response => {
                            if (handleApiError(response, navigate)) {
                                return null;
                            }
                            return safeJsonParse(response);
                        })
                        .then(membersData => {
                            return {
                                groupId: ug.group._id,
                                groupName: ug.group.name,
                                members: membersData?.members || []
                            };
                        });
                });

                Promise.all(groupPromises).then(results => {
                    // Set groups with member counts
                    setGroupsSelected(results.map((result: any) => ({
                        id: result.groupId,
                        label: result.groupName,
                        memberCount: result.members.length
                    })));

                    // Set group members
                    results.forEach((result: any) => {
                        if (result.members.length > 0) {
                            setGroupMembers(prev => ({
                                ...prev,
                                [result.groupId]: result.members.map((m: any) => ({
                                    id: m.id,
                                    email: m.email,
                                    name: m.name || '',
                                    label: obscureEmail(m.email) + (m.name ? ' (' + m.name + ')' : ''),
                                    permission: m.permission
                                }))
                            }));
                        }
                    });
                });
            });
    }, []);

    return (
        <Overlay title={t("Share task")} onClose={handleClose} className="share-task-container">
            <p>
                {parse(t("You can share the task <strong>{{taskName}}</strong> with other people.", { taskName: props.task.name }))}
            </p>
            <Tabs id="share-tabs" defaultActiveKey={'concrete-users'} activeKey={ tabKey } onSelect={(k) => setTabKey(k ?? 'concrete-users')}>
                <Tab eventKey="concrete-users" title={ t("Share with users") }>
                    { user.org != null &&
                        <Form.Check
                            type="switch"
                            id="share-with-org"
                            label={ t("Shared with someone at") + ' ' + user.org.name }
                            onChange={ handleShareWithOrgChange }
                            defaultChecked = { false }
                        />
                    }
                    <div className="share-options-container">
                        <div className="search">
                            <Form.Control type="text" placeholder={ t("Search by email or name...") } onChange={ handleUsersSearch } />
                        </div>
                        { usersFound.length > 0 &&
                            <div className="users">
                                <div className="found">
                                    <div className="label">
                                    { parse(t("Select the users with whom you want to <strong>share the task</strong>:")) }
                                    </div>
                                    <ul>
                                        { usersFound.filter((f: any) => usersSelected.findIndex((s: any) => s.id == f.id) === -1).map((u: any) => {
                                            return <li key={ `userFound-${u.id}` }>
                                                <span>
                                                    { u.label } <a className="select-user" onClick={ handleSelectUser } id={ `userFound-${u.id}`}></a>
                                                </span>
                                            </li>;
                                        })}
                                    </ul>
                                </div>
                            </div>
                        }
                        { usersSelected.length > 0 &&
                            <div className="shared">
                                <div className="label">
                                    { parse(t("Task is shared with the following users:")) }
                                </div>
                                <ul className="shared-users-list">
                                    { usersSelected.map((u: TaskShareUser) => {
                                        return <li key={ `userShared-${u.id}` } className="shared-user-item">
                                            <div className="user-info">
                                                <span className="user-label">{ u.label }</span>
                                                <a className="unselect-user" onClick={ handleUnselectUser } id={ `userShared-${u.id}`}></a>
                                            </div>
                                            <div className="user-permission">
                                                <Form.Select
                                                    size="sm"
                                                    value={u.permission}
                                                    onChange={(e) => handleUserPermissionChange(u.id, e.target.value as SharePermission)}
                                                    className="permission-select"
                                                >
                                                    <option value="view">{t("Can view")}</option>
                                                    <option value="edit">{t("Can edit")}</option>
                                                </Form.Select>
                                            </div>
                                        </li>;
                                    })}
                                </ul>
                            </div>
                        }
                    </div>
                </Tab>
                <Tab eventKey="group-users" title={ t("Share with groups") }>
                    <div className="share-options-container">
                        { groupsFound.length > 0 &&
                            <div className="groups">
                                <div className="found">
                                    <div className="label">
                                        { parse(t("Select the groups you want to <strong>share the task</strong>:")) }
                                    </div>
                                    <ul>
                                        { groupsFound.filter((f: UsersGroup) => groupsSelected.findIndex((s: any) => s.id == f._id) === -1).map((ug: any) => {
                                            // Support both new (members) and legacy (users) structure
                                            const count = ug.memberCount || ug.members?.length || ug.users?.length || 0;
                                            return <li key={ `groupFound-${ug._id}` }>
                                                <span>
                                                    { ug.name } <span className="members-count">({count} {t("members")})</span> <a className="select-group" onClick={ handleSelectGroup } id={ `groupFound-${ug._id}`}></a>
                                                </span>
                                            </li>;
                                        })}
                                    </ul>
                                </div>
                            </div>
                        }
                        { groupsSelected.length > 0 &&
                            <div className="shared">
                                <div className="label">
                                    { parse(t("Task is shared with the following groups:")) }
                                </div>
                                <ul className="shared-groups-list">
                                    { groupsSelected.map((g: GroupShareInfo) => {
                                        const isExpanded = expandedGroup === g.id;
                                        const members = groupMembers[g.id] || [];
                                        return <li key={ `userGroup-${g.id}` } className="shared-group-item">
                                            <div className="group-header">
                                                <div className="group-info">
                                                    <span className="group-label">{ g.label }</span>
                                                    <span className="members-count">({g.memberCount} {t("members")})</span>
                                                    <a className="unselect-group" onClick={ handleUnselectGroup } id={ `groupShared-${g.id}`}></a>
                                                </div>
                                                <button
                                                    type="button"
                                                    className={`expand-group-btn ${isExpanded ? 'expanded' : ''}`}
                                                    onClick={() => handleToggleGroupExpand(g.id)}
                                                    title={isExpanded ? t("Hide members") : t("Show members")}
                                                >
                                                    {isExpanded ? '▼' : '▶'}
                                                </button>
                                            </div>
                                            { isExpanded && members.length > 0 &&
                                                <div className="group-members">
                                                    <div className="members-label">
                                                        {t("Group members and their permissions:")}
                                                    </div>
                                                    <ul className="members-list">
                                                        { members.map((member: any) => {
                                                            return <li key={`member-${g.id}-${member.id}`} className="member-item">
                                                                <span className="member-label">{member.label}</span>
                                                                <span className={`member-permission-badge ${member.permission === 'edit' ? 'edit' : 'view'}`}>
                                                                    {member.permission === 'edit' ? t("Can edit") : t("Can view")}
                                                                </span>
                                                            </li>;
                                                        })}
                                                    </ul>
                                                    <p className="text-muted small mt-2">
                                                        {t("To change member permissions, edit the group in the Groups section.")}
                                                    </p>
                                                </div>
                                            }
                                            { isExpanded && members.length === 0 &&
                                                <div className="group-members">
                                                    <p className="text-muted">{t("Loading members...")}</p>
                                                </div>
                                            }
                                        </li>;
                                    })}
                                </ul>
                            </div>
                        }
                    </div>
                </Tab>
            </Tabs>
            <div className="actions">
                <Button className="btn btn-primary" onClick={ handleShare }>{ t("Share") }</Button>
                <Button className="btn btn-warning" onClick={ handleClose }>{ t("Cancel") }</Button>
            </div>
        </Overlay>
    );
}
